import { SavedHazardAssessment, HazardTypeRecord } from "../../../models/ReferenceData/HazardAssessment";
import { AnyAction } from "redux";
import {
  SAVE_HAZARD_ASSESSMENT,
  ADD_HAZARD_QUESTION_RESPONSE,
  REMOVE_HAZARD_QUESTION_RESPONSE,
  SAVE_GET_HAZARD_API_RESPONSE,
  SAVE_HHSRS_ASSESSMENT_COMPLETED,
  SAVE_POST_HAZARD_ASSESSMENT_RESPONSE,
  SAVE_PRE_ASSESSMENT_QUESTIONS_RESPONSE,
  SAVE_PRE_ASSESSMENT_RETURN_QUESTION,
  SAVE_STANDARD_REPAIR_PRIORITY,
  UPDATE_HAZARD_QUESTION_RESPONSE,
  SAVE_HAZARD_DETAILS,
  SAVE_ACTIVE_HAZARD_CASE,
  SAVE_ACTIVE_POP_HAZARD_CASE_FLAG,
  HAS_ACTIVE_HAZARD_CASE
} from "../actionTypes";
import { Case } from "../../../models/Case/CaseResponse";

interface HazardQuestionResponse {
  sequence: number;
  question: string;
  answers: string[];
  answerCodes: string[];
  code: string;
  isPreAssessment?: boolean;
}

export interface StandardRepairPriority {
  priorityCode: string;
  priorityOverride: string;
  workOrderPriority: string;
  isEmergency: boolean;
}

export interface HazardAssessmentState {
  responses: HazardQuestionResponse[];
  assessment: any;
  isHHSRSAssessmentCompleted: boolean;
  hazardQuestions: any[];
  preAssessmentReturnQuestion: { code: string; from: string };
  preAssessmentJourneyQuestions: any[] | null;
  standardRepairPriority: StandardRepairPriority | null;
  savedAssessment: SavedHazardAssessment | null;
  hazardDetails: HazardTypeRecord[];
  activeHazardCaseDetails: Case | null;
  activePopHazardCaseFlag: boolean;
  hasActiveHazardCase: boolean
}

const dependencyResponsesMatch = (
  configuredResponses: string[] = [],
  actualCodes: string[] = [],
  sourceQuestionType?: string,
): boolean => {
  const normalisedConfiguredResponses = configuredResponses.map(String);
  const normalisedActualCodes = new Set(actualCodes.map(String));

  if (sourceQuestionType === "CHECKBOX") {
    return normalisedConfiguredResponses.some((code) =>
      normalisedActualCodes.has(code),
    );
  }

  return normalisedConfiguredResponses.some((code) =>
    normalisedActualCodes.has(code),
  );
};

const isQuestionApplicable = (
  question: any,
  responses: HazardQuestionResponse[],
  sourceQuestions: any[],
): boolean => {
  if (!question?.dependsOn?.length) {
    return true;
  }

  return question.dependsOn.some((dependency: any) => {
    const parentResponse = responses.find(
      (response: HazardQuestionResponse) => response.code === dependency.code,
    );
    const sourceQuestionType = sourceQuestions.find(
      (sourceQuestion: any) => sourceQuestion.code === dependency.code,
    )?.type;

    return dependencyResponsesMatch(
      dependency.responses,
      parentResponse?.answerCodes || [],
      sourceQuestionType,
    );
  });
};

const initialState: HazardAssessmentState = {
  responses: [],
  assessment: null,
  isHHSRSAssessmentCompleted: false,
  hazardQuestions: [],
  preAssessmentReturnQuestion: { code: "", from: "" },
  preAssessmentJourneyQuestions: null,
  standardRepairPriority: null,
  savedAssessment: null,
  hazardDetails: [],
  activeHazardCaseDetails: null,
  activePopHazardCaseFlag: false,
  hasActiveHazardCase: false
};

export const hazardReducer = (state: any = initialState, action: AnyAction) => {
  switch (action.type) {
    case ADD_HAZARD_QUESTION_RESPONSE:
      return {
        ...state,
        responses: [...state.responses, action.payload],
      };

    // Updating question seq according to depends on
    case UPDATE_HAZARD_QUESTION_RESPONSE: {
      const updatedResponses = state.responses.map((item: any) =>
        item.code === action.payload.code
          ? {
            ...item,
            answers: action.payload.answers,
            answerCodes: action.payload.answerCodes,
          }
          : item,
      );

      const allQuestions = state.hazardQuestions || [];

      let filteredResponses = updatedResponses;
      const journeyQuestions = [...(state.preAssessmentJourneyQuestions || []), ...allQuestions];
      const removeDependentResponses = (questionCode: string) => {
        journeyQuestions.forEach((question: any) => {
          if (question.dependsOn?.some((dependency: any) => dependency.code === questionCode) &&
            filteredResponses.some((response: any) => response.code === question.code) &&
            !isQuestionApplicable(question, filteredResponses, journeyQuestions)) {
            filteredResponses = filteredResponses.filter((response: any) => response.code !== question.code);
            removeDependentResponses(question.code);
          }
        });
      };

      allQuestions.forEach((dependentQuestion: any) => {
        if (!dependentQuestion.dependsOn?.length) {
          return;
        }

        dependentQuestion.dependsOn.forEach((dependency: any) => {
          if (dependency.code !== action.payload.code) {
            return;
          }

          const selectedAnswers = Array.isArray(action.payload.answerCodes)
            ? action.payload.answerCodes.map(String)
            : [String(action.payload.answerCodes)];

          const dependencyResponses = Array.isArray(dependency.responses)
            ? dependency.responses
            : [];

          const dependencyMatched = dependencyResponses.some(
            (responseCode: string) =>
              selectedAnswers.includes(String(responseCode)),
          );

          if (!dependencyMatched) {
            return;
          }

          const parentQuestion = allQuestions.find(
            (q: any) => q.code === dependency.code,
          );

          if (!parentQuestion) {
            return;
          }

          const questionsBetween = allQuestions.filter(
            (q: any) =>
              q.sequence > parentQuestion.sequence &&
              q.sequence < dependentQuestion.sequence,
          );

          const codesToRemove = new Set(
            questionsBetween.map((q: any) => q.code),
          );

          filteredResponses = filteredResponses.filter(
            (response: any) => !codesToRemove.has(response.code),
          );
          codesToRemove.forEach(removeDependentResponses);
        });
      });

      removeDependentResponses(action.payload.code);

      return {
        ...state,
        responses: filteredResponses.map((item: any) => {
          const matchingQuestion = allQuestions.find(
            (question: any) => question.code === item.code,
          );

          return {
            ...item,
            sequence: matchingQuestion?.sequence ?? item.sequence,
          };
        }),
      };
    }
    // End update  
    case SAVE_GET_HAZARD_API_RESPONSE:
      return {
        ...state,
        hazardQuestions: action.payload,
      };

    case REMOVE_HAZARD_QUESTION_RESPONSE:
      return {
        ...state,
        responses: initialState.responses,
        assessment: initialState.assessment,
        isHHSRSAssessmentCompleted: initialState.isHHSRSAssessmentCompleted,
        hazardQuestions: initialState.hazardQuestions,
        preAssessmentReturnQuestion: initialState.preAssessmentReturnQuestion,
        preAssessmentJourneyQuestions: initialState.preAssessmentJourneyQuestions,
        standardRepairPriority: initialState.standardRepairPriority,
        savedAssessment: initialState.savedAssessment,
        activeHazardCaseDetails:initialState.activeHazardCaseDetails,
        hasActiveHazardCase:initialState.hasActiveHazardCase
      };

    case SAVE_POST_HAZARD_ASSESSMENT_RESPONSE:
      return {
        ...state,
        assessment: action.payload,
      };

    case SAVE_HHSRS_ASSESSMENT_COMPLETED:
      return {
        ...state,
        isHHSRSAssessmentCompleted: action.payload,
      };

    case SAVE_PRE_ASSESSMENT_RETURN_QUESTION:
      return {
        ...state,
        preAssessmentReturnQuestion: action.payload,
      };

    case SAVE_PRE_ASSESSMENT_QUESTIONS_RESPONSE:
      return {
        ...state,
        preAssessmentJourneyQuestions: action.payload,
      };

    case SAVE_STANDARD_REPAIR_PRIORITY:
      return {
        ...state,
        standardRepairPriority: action.payload,
      };
    case SAVE_HAZARD_DETAILS:
      return {
        ...state,
        hazardDetails: action.payload,
      };
    case SAVE_ACTIVE_HAZARD_CASE:
      return {
        ...state,
        activeHazardCaseDetails: action.payload,
      };

    case SAVE_ACTIVE_POP_HAZARD_CASE_FLAG:
      return {
        ...state,
        activePopHazardCaseFlag: action.payload,
      };

    case SAVE_HAZARD_ASSESSMENT:
      return { ...state, savedAssessment: action.payload };
    case HAS_ACTIVE_HAZARD_CASE:
      return {
        ...state,
        hasActiveHazardCase: action.payload,
      };
    default:
      return state;
  }
};
export default hazardReducer;